import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BlindKingsGame,
  GAME_ACTIONS,
  attackPiece,
  movePiece,
  type LastAction as CoreLastAction,
  type RawPiece,
} from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import type { LastAction as CharLastAction } from '../../../characters';
import { useSnackBarContext } from '../../../contexts/SnackBarContext';
import type { GameRecord } from './use-game-state';

export type PlayRequest = {
  action: CharLastAction;
  settleBoard: RawPiece[][];
};

export type BoardFocus = {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
};

export type OptimisticSnapshot = {
  board: RawPiece[][];
  graveyard: RawPiece[];
  following: Record<string, unknown>;
  turnNumber: number;
  turnsWithoutAction: number;
};

function cloneBoard(board: RawPiece[][] | undefined): RawPiece[][] {
  return (board ?? []).map((row) => row.map((cell) => ({ ...cell })));
}

function snapshotFromGame(game: BlindKingsGame): OptimisticSnapshot {
  return {
    board: cloneBoard(game.board),
    graveyard: [...(game.graveyard ?? [])],
    following: { ...(game.following ?? {}) },
    turnNumber: game.turnNumber,
    turnsWithoutAction: game.turnsWithoutAction,
  };
}

function readFocus(
  focus: BoardFocus | undefined
): BoardFocus | undefined {
  if (!focus?.id || !focus.from || !focus.to) return undefined;
  return {
    id: String(focus.id),
    from: { x: Number(focus.from.x), y: Number(focus.from.y) },
    to: { x: Number(focus.to.x), y: Number(focus.to.y) },
  };
}

function toCharLastAction(
  action: CoreLastAction | CharLastAction | Record<string, unknown> | undefined
): CharLastAction | undefined {
  if (!action || typeof action !== 'object') return undefined;
  const id = String((action as { id?: unknown }).id ?? '');
  const type = String((action as { type?: unknown }).type ?? '');
  if (!id || (type !== 'flip' && type !== 'move' && type !== 'attack')) {
    return undefined;
  }
  const from = (action as { from?: { x?: number; y?: number } }).from;
  const to = (action as { to?: { x?: number; y?: number } }).to;
  if (!from || !to) return undefined;
  return {
    id,
    type,
    pieceId: Number((action as { pieceId?: number }).pieceId),
    pieceValue: Number((action as { pieceValue?: number }).pieceValue),
    pieceTeamIndex: Number((action as { pieceTeamIndex?: number }).pieceTeamIndex ?? 0),
    from: { x: Number(from.x), y: Number(from.y) },
    to: { x: Number(to.x), y: Number(to.y) },
    direction: (action as { direction?: string }).direction,
    targetPieceId: (action as { targetPieceId?: number }).targetPieceId,
    targetValue: (action as { targetValue?: number }).targetValue,
    targetTeamIndex: (action as { targetTeamIndex?: number }).targetTeamIndex,
  };
}

export type UseOptimisticGameActionsOptions = {
  gameId: string | undefined;
  serverGame: BlindKingsGame;
  applyGameUpdate?: (payload: GameRecord) => void;
  playerId?: string;
  lastFocus?: BoardFocus;
  lastAction?: CoreLastAction | CharLastAction | Record<string, unknown>;
};

export default function useOptimisticGameActions({
  gameId,
  serverGame,
  applyGameUpdate,
  lastFocus,
  lastAction,
}: UseOptimisticGameActionsOptions) {
  const { addSnackBar } = useSnackBarContext();
  const [canvasBoard, setCanvasBoard] = useState<RawPiece[][]>(() =>
    cloneBoard(serverGame.board)
  );
  const [optimistic, setOptimistic] = useState<OptimisticSnapshot | null>(null);
  const [pendingFlipPieceId, setPendingFlipPieceId] = useState<number | undefined>();
  const [playRequest, setPlayRequest] = useState<PlayRequest | null>(null);
  const [remoteFocus, setRemoteFocus] = useState<BoardFocus | undefined>();
  const [inputLocked, setInputLocked] = useState(false);

  const localFocusIdsRef = useRef<Set<string>>(new Set());
  const seenFocusIdsRef = useRef<Set<string>>(new Set());
  const bootstrappedRef = useRef(false);
  const revertRef = useRef<OptimisticSnapshot | null>(null);
  const apiPendingRef = useRef(false);
  const animPendingRef = useRef(false);
  const pendingFocusRef = useRef<BoardFocus | undefined>(undefined);
  /** Board shown before the latest remote settle — used to animate opponent moves. */
  const preRemoteBoardRef = useRef<RawPiece[][]>(cloneBoard(serverGame.board));

  const clearOptimisticUi = useCallback(() => {
    setPendingFlipPieceId(undefined);
    setOptimistic(null);
    revertRef.current = null;
  }, []);

  const tryUnlock = useCallback(() => {
    if (apiPendingRef.current || animPendingRef.current) return;
    clearOptimisticUi();
    setInputLocked(false);
  }, [clearOptimisticUi]);

  const clearActionIndicators = useCallback(() => {
    pendingFocusRef.current = undefined;
    setRemoteFocus(undefined);
  }, []);

  const displayGame = useMemo(() => {
    if (!optimistic) return serverGame;
    return new BlindKingsGame({
      firstPlayer: serverGame.firstPlayer,
      secondPlayer: serverGame.secondPlayer,
      board: optimistic.board,
      graveyard: optimistic.graveyard,
      following: optimistic.following,
      turnNumber: optimistic.turnNumber,
      turnsWithoutAction: optimistic.turnsWithoutAction,
      lastActionPlayerIndex: serverGame.lastActionPlayerIndex,
      firstPlayerQuit: serverGame.firstPlayerQuit,
      secondPlayerQuit: serverGame.secondPlayerQuit,
      timeFinished: serverGame.timeFinished,
      turnFinishBy: serverGame.turnFinishBy,
      gameMode: serverGame.gameMode,
      maxTurnsWithoutAction: serverGame.maxTurnsWithoutAction,
      maxFollowingTurns: serverGame.maxFollowingTurns,
      turnLength: serverGame.turnLength,
      userPlayerId: serverGame.userPlayerId,
    });
  }, [serverGame, optimistic]);

  useEffect(() => {
    if (inputLocked || playRequest || optimistic) return;
    const focus = readFocus(lastFocus);
    const action = toCharLastAction(lastAction);
    const pendingRemoteId = action?.id ?? focus?.id;
    if (
      pendingRemoteId &&
      !seenFocusIdsRef.current.has(pendingRemoteId) &&
      !localFocusIdsRef.current.has(pendingRemoteId)
    ) {
      // Hold the pre-move board until the remote anim effect claims this action.
      return;
    }
    const next = cloneBoard(serverGame.board);
    setCanvasBoard(next);
    preRemoteBoardRef.current = next;
  }, [
    serverGame.board,
    lastFocus?.id,
    lastAction,
    inputLocked,
    playRequest,
    optimistic,
  ]);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    const focus = readFocus(lastFocus);
    if (focus?.id) {
      seenFocusIdsRef.current.add(focus.id);
      setRemoteFocus(focus);
    }
    const action = toCharLastAction(lastAction);
    if (action?.id) {
      seenFocusIdsRef.current.add(action.id);
    }
    preRemoteBoardRef.current = cloneBoard(serverGame.board);
  }, [lastFocus, lastAction, serverGame.board]);

  useEffect(() => {
    const focus = readFocus(lastFocus);
    const action = toCharLastAction(lastAction);
    const actionId = action?.id ?? focus?.id;
    if (!actionId) {
      if (!animPendingRef.current) {
        pendingFocusRef.current = undefined;
        setRemoteFocus(undefined);
      }
      return;
    }
    if (localFocusIdsRef.current.has(actionId)) return;
    if (seenFocusIdsRef.current.has(actionId)) return;
    if (playRequest || inputLocked) return;

    seenFocusIdsRef.current.add(actionId);

    const settleBoard = cloneBoard(serverGame.board);
    if (action) {
      setRemoteFocus(undefined);
      if (focus) pendingFocusRef.current = focus;
      // Keep previous positions on canvas so ActionAnimator can tween from → to.
      setCanvasBoard(cloneBoard(preRemoteBoardRef.current));
      animPendingRef.current = true;
      setInputLocked(true);
      setPlayRequest({
        action,
        settleBoard,
      });
      return;
    }

    if (focus) setRemoteFocus(focus);
    setCanvasBoard(settleBoard);
    preRemoteBoardRef.current = settleBoard;
  }, [
    lastFocus?.id,
    lastAction,
    serverGame.board,
    playRequest,
    inputLocked,
  ]);

  // Safety: never leave the board soft-locked if an anim callback is missed.
  useEffect(() => {
    if (!inputLocked) return;
    const id = window.setTimeout(() => {
      if (!apiPendingRef.current) {
        animPendingRef.current = false;
        const pending = pendingFocusRef.current;
        pendingFocusRef.current = undefined;
        if (pending) setRemoteFocus(pending);
        clearOptimisticUi();
        setPlayRequest(null);
        setInputLocked(false);
        setCanvasBoard(cloneBoard(serverGame.board));
        preRemoteBoardRef.current = cloneBoard(serverGame.board);
      }
    }, 4000);
    return () => window.clearTimeout(id);
  }, [inputLocked, serverGame.board, clearOptimisticUi]);

  const applyOrDeferFocus = useCallback((focus: BoardFocus | undefined) => {
    if (!focus) return;
    if (animPendingRef.current) {
      pendingFocusRef.current = focus;
      return;
    }
    setRemoteFocus(focus);
  }, []);

  const trackServerFocus = useCallback((result: GameRecord) => {
    const focus = readFocus(result.lastFocus);
    if (focus?.id) {
      localFocusIdsRef.current.add(focus.id);
      seenFocusIdsRef.current.add(focus.id);
      applyOrDeferFocus(focus);
    }
    const action = toCharLastAction(result.lastAction);
    if (action?.id) {
      localFocusIdsRef.current.add(action.id);
      seenFocusIdsRef.current.add(action.id);
    }
  }, [applyOrDeferFocus]);

  const onPlayComplete = useCallback(
    (actionId: string) => {
      setPlayRequest((current) => {
        if (current?.action.id === actionId) {
          const settled = cloneBoard(current.settleBoard);
          setCanvasBoard(settled);
          preRemoteBoardRef.current = settled;
          return null;
        }
        return current;
      });
      animPendingRef.current = false;
      const pending = pendingFocusRef.current;
      pendingFocusRef.current = undefined;
      if (pending) setRemoteFocus(pending);
      tryUnlock();
    },
    [tryUnlock]
  );

  const dispatchMoveOrAttack = useCallback(
    async (args: {
      action: string;
      pieceId: number;
      direction: string;
      gamePiece: { id: number; value: number; teamIndex?: number; location: { x: number; y: number } };
      target?: { id: number; value: number; teamIndex?: number; location: { x: number; y: number } };
    }) => {
      if (!gameId || inputLocked) return;
      const pre = snapshotFromGame(displayGame);
      revertRef.current = pre;
      preRemoteBoardRef.current = cloneBoard(pre.board);

      const gamePiece = displayGame.gameBoard.gamePiecesById[args.pieceId];
      if (!gamePiece) return;

      const update = (
        args.action === GAME_ACTIONS.ATTACK
          ? attackPiece({
              blindKingsGame: displayGame,
              gamePiece,
              direction: args.direction,
            })
          : movePiece({
              blindKingsGame: displayGame,
              gamePiece,
              direction: args.direction,
            })
      ) as {
        board: RawPiece[][];
        graveyard?: RawPiece[];
        following?: Record<string, unknown>;
        turnNumber: number;
        turnsWithoutAction: number;
      };

      const to =
        args.action === GAME_ACTIONS.ATTACK
          ? args.target?.location ?? args.gamePiece.location
          : gamePiece.getPieceToMoveToByDirection(args.direction)?.location ??
            args.gamePiece.location;

      const actionId = `local-${args.action}-${args.pieceId}-${Date.now()}`;
      localFocusIdsRef.current.add(actionId);

      const lastActionLocal: CharLastAction = {
        id: actionId,
        type: args.action === GAME_ACTIONS.ATTACK ? 'attack' : 'move',
        pieceId: args.pieceId,
        pieceValue: args.gamePiece.value,
        pieceTeamIndex: args.gamePiece.teamIndex ?? 0,
        from: { ...args.gamePiece.location },
        to: { ...to },
        direction: args.direction,
        targetPieceId: args.target?.id,
        targetValue: args.target?.value,
        targetTeamIndex: args.target?.teamIndex,
      };

      apiPendingRef.current = true;
      animPendingRef.current = true;
      setRemoteFocus(undefined);
      pendingFocusRef.current = {
        id: actionId,
        from: { ...args.gamePiece.location },
        to: { ...to },
      };
      setOptimistic({
        board: cloneBoard(update.board),
        graveyard: update.graveyard ?? pre.graveyard,
        following: update.following ?? pre.following,
        turnNumber: update.turnNumber,
        turnsWithoutAction: update.turnsWithoutAction,
      });
      setPlayRequest({
        action: lastActionLocal,
        settleBoard: cloneBoard(update.board),
      });
      setInputLocked(true);

      try {
        const result = (await api.dispatch(gameId, {
          action: args.action,
          pieceId: args.pieceId,
          direction: args.direction,
        })) as GameRecord;

        trackServerFocus(result);
        applyGameUpdate?.(result);
        apiPendingRef.current = false;
        tryUnlock();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        addSnackBar({
          title: 'ERROR',
          message,
          variant: 'danger',
          timeout: 3000,
        });
        apiPendingRef.current = false;
        animPendingRef.current = false;
        if (revertRef.current) {
          setCanvasBoard(cloneBoard(revertRef.current.board));
          preRemoteBoardRef.current = cloneBoard(revertRef.current.board);
        }
        setPlayRequest(null);
        clearOptimisticUi();
        setInputLocked(false);
      }
    },
    [
      gameId,
      inputLocked,
      displayGame,
      applyGameUpdate,
      addSnackBar,
      tryUnlock,
      clearOptimisticUi,
      trackServerFocus,
    ]
  );

  const dispatchFlip = useCallback(
    async (pieceId: number) => {
      if (!gameId || inputLocked) return;
      const piece = displayGame.gameBoard.gamePiecesById[pieceId];
      if (!piece) return;

      preRemoteBoardRef.current = cloneBoard(displayGame.board);
      apiPendingRef.current = true;
      animPendingRef.current = false;
      setRemoteFocus(undefined);
      pendingFocusRef.current = undefined;
      setPendingFlipPieceId(pieceId);
      setInputLocked(true);

      try {
        const result = (await api.dispatch(gameId, {
          action: GAME_ACTIONS.FLIP,
          pieceId,
        })) as GameRecord;

        const decoded = result.encodedGameState
          ? BlindKingsGame.decodeGameState(String(result.encodedGameState))
          : result;
        const settleBoard = cloneBoard(
          (decoded.board as RawPiece[][]) ?? (result.board as RawPiece[][]) ?? displayGame.board
        );

        const focus = readFocus(result.lastFocus);
        const actionId = focus?.id ?? `local-flip-${pieceId}-${Date.now()}`;
        localFocusIdsRef.current.add(actionId);
        seenFocusIdsRef.current.add(actionId);
        pendingFocusRef.current = focus ?? {
          id: actionId,
          from: { ...piece.location },
          to: { ...piece.location },
        };

        const flipped = settleBoard[piece.location.y]?.[piece.location.x];
        const lastActionLocal: CharLastAction = {
          id: actionId,
          type: 'flip',
          pieceId,
          pieceValue: flipped?.value ?? piece.value,
          pieceTeamIndex: flipped?.teamIndex ?? 0,
          from: { ...piece.location },
          to: { ...piece.location },
        };

        applyGameUpdate?.(result);
        apiPendingRef.current = false;
        animPendingRef.current = true;
        setOptimistic({
          board: settleBoard,
          graveyard: [...((decoded.graveyard as RawPiece[]) ?? displayGame.graveyard)],
          following: {
            ...((decoded.following as Record<string, unknown>) ?? displayGame.following),
          },
          turnNumber: Number(decoded.turnNumber ?? displayGame.turnNumber + 1),
          turnsWithoutAction: Number(decoded.turnsWithoutAction ?? 0),
        });
        setPlayRequest({ action: lastActionLocal, settleBoard });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        addSnackBar({
          title: 'ERROR',
          message,
          variant: 'danger',
          timeout: 3000,
        });
        apiPendingRef.current = false;
        animPendingRef.current = false;
        clearOptimisticUi();
        setInputLocked(false);
      }
    },
    [gameId, inputLocked, displayGame, applyGameUpdate, addSnackBar, clearOptimisticUi]
  );

  return {
    displayGame,
    canvasBoard,
    pendingFlipPieceId,
    playRequest,
    remoteFocus,
    inputLocked,
    onPlayComplete,
    dispatchMoveOrAttack,
    dispatchFlip,
    clearActionIndicators,
  };
}
